import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * Tracks user activity and flags the client as idle after `timeoutMs` without input.
 * @param {number} timeoutMs - Idle duration before `onIdle` fires.
 * @param {Function} onIdle - Called once when the idle timeout is reached.
 * @returns {{ isIdle: boolean }} True once the idle timeout has elapsed.
 */
export function useIdleTimeout(timeoutMs, onIdle) {
  const [isIdle, setIsIdle] = useState(false);
  const isIdleRef = useRef(false);
  const timerRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const startTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      setIsIdle(true);
      onIdleRef.current?.();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    const onActivity = () => {
      if (!isIdleRef.current) startTimer();
    };
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));
    startTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
      clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  return { isIdle };
}
